import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const STORAGE_KEY = 'cosebu.theme';

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

const prefersDark = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

const readStored = () => {
    if (typeof window === 'undefined') return null;
    try {
        const value = window.localStorage.getItem(STORAGE_KEY);
        return value === 'dark' || value === 'light' ? value : null;
    } catch {
        return null;
    }
};

export const ThemeProvider = ({ children }) => {
    // Respect the operating system first; a stored choice always wins.
    const [isDark, setIsDark] = useState(() => {
        const stored = readStored();
        if (stored) return stored === 'dark';
        return prefersDark();
    });

    // Follow the OS while the visitor has not expressed a preference.
    useEffect(() => {
        if (readStored()) return undefined;
        const query = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (event) => {
            if (!readStored()) setIsDark(event.matches);
        };
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    // Single source of truth for the document theme class.
    useEffect(() => {
        const root = document.documentElement;
        document.body.classList.toggle('theme-dark', isDark);
        document.body.classList.toggle('theme-light', !isDark);
        root.style.colorScheme = isDark ? 'dark' : 'light';
        // The anti-flash script paints the root; keep it in sync on toggle.
        root.style.backgroundColor = isDark ? '#100e0b' : '#faf8f5';
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute('content', isDark ? '#100e0b' : '#faf8f5');
    }, [isDark]);

    const toggleTheme = useCallback(() => {
        setIsDark((previous) => {
            const next = !previous;
            try {
                window.localStorage.setItem(STORAGE_KEY, next ? 'dark' : 'light');
            } catch {
                /* storage unavailable - the in-memory choice still applies */
            }
            return next;
        });
    }, []);

    return (
        <ThemeContext.Provider value={{ isDark, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
